import { TrendingUp, Facebook, Instagram, Linkedin, Youtube, Mail, Phone, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';

const Footer = () => {
  const quickLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About Us' },
    { path: '/services', label: 'Services' },
    { path: '/calculator', label: 'Calculator' },
    { path: '/contact', label: 'Contact' }
  ];
  
  const services = [
    'Equity Trading',
    'Mutual Funds',
    'Portfolio Management (PMS)',
    'Corporate Bonds',
    'Gold Bonds',
    'NBFC Fixed Deposits'
  ];
  
  const socialLinks = [
    { icon: Facebook, href: 'https://www.facebook.com/mfrumtek', label: 'Facebook' },
    { icon: Instagram, href: 'https://www.instagram.com/mfrumtek/', label: 'Instagram' },
    { icon: Linkedin, href: 'https://www.linkedin.com/in/mfrumtek/', label: 'LinkedIn' },
    { icon: Youtube, href: 'https://www.youtube.com/@mfrumtek', label: 'YouTube' }
  ];

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div data-testid="footer-company-info">
            <div className="flex items-center mb-6">
              <img 
                src="/rumtek-logo.jpg" 
                alt="Rumtek Finvest Pvt Ltd - Leading financial advisor in Kolkata India with 25+ years experience in wealth management and investment advisory"
                className="h-24 w-auto"
                loading="lazy"
              />
            </div>
            
            {/* Contact Information */}
            <div className="text-sm text-gray-300 space-y-2 mb-4">
              <div className="bg-blue-600 text-white px-3 py-1 rounded font-semibold text-center">
                📞 +91 70440 55500
              </div>
              <div>
                <strong>Address:</strong><br/>
                15 S. P. Mukherjee Road, 3rd Floor<br/>
                Kolkata 700025<br/>
                West Bengal, India<br/>
                (Opposite Purna Cinema, above IDBI Bank)
              </div>
              <div>
                <strong>Office:</strong> 033-35062626<br/>
                <strong>Email:</strong> rumtekmf@gmail.com
              </div>
            </div>

            {/* Authorization */}
            <div className="bg-gray-800 p-3 rounded-lg text-xs text-gray-300">
              <p className="font-semibold mb-1">RUMTEK Finvest Pvt Ltd</p>
              <p>is an Authorized Person of</p>
              <p className="font-semibold">SUNIDHI SECURITIES AND FINANCE LTD.</p>
            </div>
            <a
              href="https://fip.rumtek.co/login"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-block bg-amber-500 hover:bg-amber-400 text-gray-900 font-bold text-sm px-5 py-2.5 rounded-lg transition-all hover:scale-105 shadow-md"
              data-testid="mfd-login-btn"
            >
              MFD Login
            </a>
          </div>
          
          {/* Quick Links */}
          <div data-testid="footer-quick-links">
            <h3 className="text-lg font-semibold mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-gray-400 hover:text-white transition-colors"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Our Associates */}
          <div data-testid="footer-associates">
            <h3 className="text-lg font-semibold mb-6">OUR ASSOCIATES:</h3>
            <div className="space-y-2 text-sm text-gray-300">
              <div>
                <strong>Amrapali Nishit Vora:</strong><br/>
                📱 9836060752
              </div>
              <div>
                <strong>Vedant Vora:</strong><br/>
                📱 9007595000
              </div>
              <div>
                <strong>Dipak P Ghelani:</strong><br/>
                📱 9903099486
              </div>
              <div>
                <strong>SR Kalidas:</strong><br/>
                📱 9830464493
              </div>
              <div>
                <strong>Paresh Ajmera:</strong><br/>
                📱 9830117287
              </div>
            </div>
          </div>
          
          {/* Contact & Social */}
          <div data-testid="footer-contact-social">
            <h3 className="text-lg font-semibold mb-6">Stay Connected</h3>
            <div className="space-y-4">
              <div className="flex space-x-4">
                {socialLinks.map((social, index) => {
                  const Icon = social.icon;
                  const brandColors = {
                    'Facebook': 'bg-blue-600 hover:bg-blue-500',
                    'Instagram': 'bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 hover:from-purple-500 hover:via-pink-400 hover:to-orange-300',
                    'LinkedIn': 'bg-blue-700 hover:bg-blue-600',
                    'YouTube': 'bg-red-600 hover:bg-red-500'
                  };
                  
                  return (
                    <a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${brandColors[social.label] || 'bg-gray-800 hover:bg-blue-600'} text-white p-3 rounded-full transition-all transform hover:scale-110 shadow-md`}
                      data-testid={`social-link-${social.label.toLowerCase()}`}
                      aria-label={social.label}
                      title={`Follow us on ${social.label}`}
                    >
                      <Icon className="h-5 w-5" />
                    </a>
                  );
                })}
              </div>
              
              <div className="bg-gray-800 p-4 rounded-lg">
                <h4 className="font-semibold mb-2">Market Hours</h4>
                <div className="text-sm text-gray-400 space-y-1">
                  <div>Monday - Friday: 9:00 AM - 6:00 PM</div>
                  <div>Saturday: 9:00 AM - 1:00 PM</div>
                  <div className="text-red-400">Sunday: Closed</div>
                </div>
              </div>
              
              <div className="bg-blue-600 p-4 rounded-lg">
                <h4 className="font-semibold mb-2">Free Consultation</h4>
                <p className="text-sm text-blue-100 mb-3">
                  Get expert investment advice tailored to your goals.
                </p>
                <Link
                  to="/book-consultation"
                  data-testid="footer-consultation-btn"
                  className="bg-white text-blue-600 px-4 py-2 rounded font-medium text-sm hover:bg-gray-100 transition-colors inline-block"
                >
                  Book Now
                </Link>
              </div>
            </div>
          </div>
        </div>
        
        {/* Legal Registration Details */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {/* Exchange Registration */}
            <div className="bg-gray-800 p-4 rounded-lg">
              <h4 className="font-semibold text-white mb-3">Exchange Registration</h4>
              <div className="space-y-2 text-sm text-gray-300">
                <div><strong>NSE:</strong> AP041204383</div>
                <div><strong>BSE:</strong> AP0102910174480</div>
              </div>
            </div>
            
            {/* Registration Numbers */}
            <div className="bg-gray-800 p-4 rounded-lg">
              <h4 className="font-semibold text-white mb-3">Registration Details</h4>
              <div className="space-y-2 text-sm text-gray-300">
                <div><strong>AMFI REG NO:</strong> 23639</div>
                <div><strong>PMS APRN CODE:</strong> APRN00365</div>
              </div>
            </div>
            
            {/* Legal Identifiers */}
            <div className="bg-gray-800 p-4 rounded-lg">
              <h4 className="font-semibold text-white mb-3">Legal Identifiers</h4>
              <div className="space-y-2 text-sm text-gray-300">
                <div><strong>GST:</strong> 19AABCR3472P1ZS</div>
                <div><strong>CIN:</strong> U67120WB1996PTC079392</div>
              </div>
            </div>
          </div>
          
          {/* Bottom Copyright and Links */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center border-t border-gray-700 pt-6">
            <div className="text-center lg:text-left">
              <p className="text-gray-400 text-sm">
                © {new Date().getFullYear()} RUMTEK FINVEST PVT. LTD. All rights reserved. 
                Established 1996 | SEBI Registered Investment Advisor
              </p>
            </div>
            <div className="text-center lg:text-right">
              <div className="flex justify-center lg:justify-end space-x-6 text-sm text-gray-400">
                <a href="#" className="hover:text-white transition-colors underline">Quick Links</a>
                <a href="/contact" className="hover:text-white transition-colors underline">Contact Us</a>
                <a href="#" className="hover:text-white transition-colors underline">Disclaimer</a>
              </div>
            </div>
          </div>
          
          {/* Investment Disclaimer */}
          <div className="mt-6 p-4 bg-gray-800 rounded-lg">
            <p className="text-xs text-gray-400 leading-relaxed">
              <strong>Investment Disclaimer:</strong> Mutual Fund investments are subject to market risks. 
              Please read all scheme-related documents carefully before investing. Past performance is not 
              indicative of future returns. The value of investments can go up as well as down. 
              Rumtek Finvest is a SEBI registered investment advisor providing advisory services.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;